import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-accordeon.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { watch } from '@lit-labs/preact-signals';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../../../layout/bh-icon-link.js';
import './bh-page-admin-global-settings-accessibility.js';
import './bh-page-admin-global-settings-colors.js';
import './bh-page-admin-global-settings-general.js';
import './bh-page-admin-global-settings-imprint.js';
import './bh-page-admin-global-settings-institution-overview-editor.js';
import './bh-page-admin-global-settings-mails.js';
import './bh-page-admin-global-settings-maintenance.js';
import './bh-page-admin-global-settings-privacy.js';
import './bh-page-admin-global-settings-terms.js';

export const adminGlobalSettingsRouter = makeRouter(
  {
    general: {
      path: '/general',
      render: () => html` <bh-page-admin-global-settings-general></bh-page-admin-global-settings-general> `,
    },
    imprint: {
      path: '/imprint',
      render: () => html` <bh-page-admin-global-settings-imprint></bh-page-admin-global-settings-imprint> `,
    },
    'terms-of-use': {
      path: '/terms-of-use',
      render: () => html` <bh-page-admin-global-settings-terms></bh-page-admin-global-settings-terms> `,
    },
    privacy: {
      path: '/privacy',
      render: () => html` <bh-page-admin-global-settings-privacy></bh-page-admin-global-settings-privacy> `,
    },
    accessibility: {
      path: '/accessibility',
      render: () => html` <bh-page-admin-global-settings-accessibility></bh-page-admin-global-settings-accessibility> `,
    },
    mails: {
      path: '/mails',
      render: () => html` <bh-page-admin-global-settings-mails></bh-page-admin-global-settings-mails> `,
    },
    maintenance: {
      path: '/maintenance',
      render: () => html` <bh-page-admin-global-settings-maintenance></bh-page-admin-global-settings-maintenance> `,
    },
    colors: {
      path: '/colors',
      render: () => html` <bh-page-admin-global-settings-colors></bh-page-admin-global-settings-colors> `,
    },
    'institution-overview-editor': {
      path: '/institution-overview-editor',
      metaData: {
        breadcrumbs: ({ translate }) => [
          { content: translate('settings', 'name'), href: '/admin/settings' },
          { content: 'Institutionsübersicht', active: true },
        ],
      },
      render: () =>
        html`
          <bh-page-admin-global-settings-institution-overview-editor></bh-page-admin-global-settings-institution-overview-editor>
        `,
    },
  },
  {
    fallback: () => {
      const design = useObservable(() => DesignSystem.currentTheme, []);
      return html`
        <x-flex space="xl">
          <bh-breadcrumb .items=${[{ content: 'Einstellungen', active: true }]}></bh-breadcrumb>
          <x-flex padding="md" space="xs">
            <bh-icon-link
              icon="date_range"
              text=${`${clientTranslator.translate('settings', 'general_information')}...`}
              .link=${`/admin/settings/general`}
            ></bh-icon-link>

            <x-flex
              horizontal
              crossaxis-center
              space="sm"
              padding="xxs"
              ${css({ color: design?.colors.accent, cursor: 'pointer' })}
              @click=${() => goTo('/admin/settings/mails')}
            >
              <x-icon> mail </x-icon>
              <x-text> ${clientTranslator.translate('global_settings', 'edit_email_templates')}... </x-text>
            </x-flex>

            <bh-icon-link
              icon="warning"
              text=${`Wartungsmodus...`}
              .link=${`/admin/settings/maintenance`}
            ></bh-icon-link>

            <bh-icon-link
              icon="palette"
              text=${`Farben definieren...`}
              .link=${`/admin/settings/colors`}
            ></bh-icon-link>

            <bh-icon-link
              icon="overview_key"
              text=${`Institutionsübersicht bearbeiten...`}
              .link=${`/admin/settings/institution-overview-editor`}
            ></bh-icon-link>

            <x-hr ${css({ height: '3px' })}></x-hr>

            <bh-icon-link
              icon="description"
              text=${`${clientTranslator.translate('termsOfUse')}...`}
              .link=${`/admin/settings/terms-of-use`}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text=${`${clientTranslator.translate('imprint')}...`}
              .link=${`/admin/settings/imprint`}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text=${`${clientTranslator.translate('privacy')}...`}
              .link=${`/admin/settings/privacy`}
            ></bh-icon-link>
            <bh-icon-link
              icon="description"
              text=${`${clientTranslator.translate('accessibility')}...`}
              .link=${`/admin/settings/accessibility`}
            ></bh-icon-link>
          </x-flex>
        </x-flex>
      `;
    },
  },
);

/**
 * @element bh-page-admin-global-settings
 */
@customElement('bh-page-admin-global-settings')
export class BHPageAdminGlobalSettings extends BuchungsheldLitElement {
  override render() {
    return permissionGuard(
      Permission.VIEW_SUPER_ADMIN_SIDEBAR,
      () => html` ${watch(adminGlobalSettingsRouter.outlet)} `,
    );
  }
}
